/**
 * Sparkline
 *
 * Inline-SVG multi-line sparkline. No chart library per CONSTITUTION.
 * Copied from user app's RetentionEngagementPanel.tsx pattern.
 */

interface SparklineSeries {
  values: number[];
  color: string;
  label: string;
}

interface SparklineProps {
  series: SparklineSeries[];
  width?: number;
  height?: number;
}

export function Sparkline({ series, width = 300, height = 60 }: SparklineProps) {
  if (!series.length || !series[0].values.length) {
    return (
      <svg width={width} height={height} aria-label="Sparkline — no data">
        <text x={width / 2} y={height / 2} textAnchor="middle" fill="#71717a" fontSize="10">
          No data
        </text>
      </svg>
    );
  }

  const allValues = series.flatMap((s) => s.values);
  const maxVal = Math.max(...allValues, 1);
  const minVal = Math.min(...allValues, 0);
  const range = maxVal - minVal || 1;
  const pad = 4;

  function toPoints(values: number[]): string {
    if (values.length === 0) return '';
    return values
      .map((v, i) => {
        const x = pad + (i / Math.max(values.length - 1, 1)) * (width - pad * 2);
        const y = pad + (1 - (v - minVal) / range) * (height - pad * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-label={series.map((s) => `${s.label}: ${s.values.join(', ')}`).join('; ')}
      role="img"
    >
      {series.map((s) => {
        const points = toPoints(s.values);
        return (
          <polyline
            key={s.label}
            points={points}
            fill="none"
            stroke={s.color}
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}
